<template>
  <div class="profile-container">
    <!-- Animated Background -->
    <div class="profile-bg">
      <div class="bg-shape shape-1"></div>
      <div class="bg-shape shape-2"></div>
    </div>

    <div class="profile-content">
      <!-- Header -->
      <div class="profile-header">
        <div class="header-content">
          <h1 class="profile-title">
            <span class="title-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </span>
            Meu Perfil
          </h1>
          <p class="profile-subtitle">Gerencie suas informações pessoais</p>
        </div>
      </div>

      <div class="profile-grid">
        <!-- Profile Card -->
        <div class="profile-card main-card">
          <div class="card-glow"></div>

          <div v-if="loading" class="loading-state">
            <div class="spinner-large"></div>
            <p class="loading-text">Carregando perfil...</p>
          </div>

          <div v-else class="profile-info">
            <!-- Avatar Section -->
            <div class="avatar-section">
              <div class="avatar-wrapper">
                <div class="avatar-ring"></div>
                <img
                  v-if="profileImageUrl"
                  :src="profileImageUrl"
                  :alt="user.username"
                  class="avatar-image"
                />
                <div v-else class="avatar-placeholder">
                  <span class="avatar-letter">
                    {{ user?.username?.charAt(0)?.toUpperCase() }}
                  </span>
                </div>
                <div class="avatar-badge">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>

                <!-- Upload Button -->
                <button
                  @click="triggerImageUpload"
                  class="avatar-upload-btn"
                  :disabled="uploadingImage"
                  title="Alterar foto de perfil"
                >
                  <svg
                    v-if="!uploadingImage"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <div v-else class="spinner-small"></div>
                </button>

                <!-- Remove Button (only if has image) -->
                <button
                  v-if="profileImageUrl && !uploadingImage"
                  @click="removeImage"
                  class="avatar-remove-btn"
                  title="Remover foto de perfil"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>

                <!-- Hidden File Input -->
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif"
                  @change="handleImageUpload"
                  style="display: none"
                />
              </div>

              <div class="user-info">
                <h2 class="user-name">{{ user?.username }}</h2>
                <p class="user-email">{{ user?.email }}</p>
              </div>
            </div>

            <!-- Stats -->
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-icon stat-icon-calendar">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div class="stat-info">
                  <p class="stat-label">Membro desde</p>
                  <p class="stat-value">{{ formatDate(user?.created_at) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Edit Card -->
        <div class="profile-card edit-card">
          <div class="card-header">
            <h2 class="card-title">
              <span class="title-icon-small">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </span>
              Editar Perfil
            </h2>
            <button v-if="!isEditing" @click="startEditing" class="edit-btn">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
              Editar
            </button>
          </div>

          <!-- View Mode -->
          <div v-if="!isEditing" class="view-mode">
            <div class="info-group">
              <div class="info-item">
                <label class="info-label">Nome de usuário</label>
                <div class="info-value">
                  <span class="value-icon">@</span>
                  <span>{{ user?.username }}</span>
                </div>
              </div>
              <div class="info-item">
                <label class="info-label">Email</label>
                <div class="info-value">
                  <span class="value-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </span>
                  <span>{{ user?.email }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Edit Mode -->
          <form v-else @submit.prevent="handleSave" class="edit-form">
            <div class="form-grid">
              <!-- Username -->
              <div class="form-group">
                <label for="editUsername" class="form-label">
                  <span class="label-icon">@</span>
                  Nome de usuário
                </label>
                <div class="input-wrapper">
                  <input
                    id="editUsername"
                    v-model="editForm.username"
                    type="text"
                    required
                    class="form-input"
                    :class="{ 'input-error': formErrors.username }"
                    placeholder="seu_usuario"
                    :disabled="loading"
                  />
                </div>
                <p v-if="formErrors.username" class="error-message">
                  {{ formErrors.username }}
                </p>
              </div>

              <!-- Email -->
              <div class="form-group">
                <label for="editEmail" class="form-label">
                  <span class="label-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </span>
                  Email
                </label>
                <div class="input-wrapper">
                  <input
                    id="editEmail"
                    v-model="editForm.email"
                    type="email"
                    required
                    class="form-input"
                    :class="{ 'input-error': formErrors.email }"
                    placeholder="seu@email.com"
                    :disabled="loading"
                  />
                </div>
                <p v-if="formErrors.email" class="error-message">
                  {{ formErrors.email }}
                </p>
              </div>
            </div>

            <!-- Error Alert -->
            <div v-if="saveError" class="error-alert">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{{ saveError }}</span>
            </div>

            <!-- Success Alert -->
            <div v-if="uploadSuccess" class="success-alert">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{{ uploadSuccess }}</span>
            </div>

            <!-- Action Buttons -->
            <div class="form-actions">
              <button
                type="button"
                @click="cancelEditing"
                :disabled="loading"
                class="btn-cancel"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="!canSave || loading"
                class="btn-save"
              >
                <span v-if="loading" class="btn-loading">
                  <div class="spinner-small"></div>
                  Salvando...
                </span>
                <span v-else class="btn-content">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Salvar Alterações
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, reactive, watch } from "vue";
import { useAuthStore } from "@/store/auth";

export default {
  name: "Profile",
  setup() {
    const authStore = useAuthStore();
    const loading = ref(false);
    const isEditing = ref(false);
    const saveError = ref("");
    const uploadingImage = ref(false);
    const fileInput = ref(null);
    const uploadSuccess = ref("");
    const user = ref(authStore.userProfile);
    const editForm = reactive({
      username: "",
      email: "",
    });

    const formErrors = reactive({
      username: "",
      email: "",
    });

    // Computed para URL completa da imagem de perfil
    const profileImageUrl = computed(() => {
      const rawUrl = user.value?.profile_image_url;
      if (!rawUrl) return null;

      // Se for base64, valida antes de usar
      if (rawUrl.startsWith("data:image/")) {
        try {
          // Verifica se o base64 está completo (mínimo 100 caracteres após o cabeçalho)
          const base64Data = rawUrl.split(",")[1];
          if (!base64Data || base64Data.length < 100) {
            console.warn("⚠️ [Profile] Base64 incompleto ou corrompido");
            return null;
          }
          return rawUrl;
        } catch (error) {
          console.error("❌ [Profile] Erro ao validar base64:", error);
          return null;
        }
      }

      // Se já for uma URL completa (https/http), usa direto
      if (rawUrl.startsWith("http")) {
        return rawUrl;
      }

      // Se for uma URL relativa (/uploads/...), constrói com a base da API
      const apiBase =
        process.env.VUE_APP_API_URL || "http://localhost:3000/api";
      const baseUrl = apiBase.replace("/api", "");
      const fullUrl = rawUrl.startsWith("/")
        ? baseUrl + rawUrl
        : baseUrl + "/" + rawUrl;
      return fullUrl;
    });

    // Watch user changes to update form when profile data is fetched
    watch(user, (newUser) => {
      if (newUser) {
        resetFormToUserData();
      }
    });

    const resetFormToUserData = () => {
      editForm.username = user.value?.username || "";
      editForm.email = user.value?.email || "";
      clearErrors();
    };

    const clearErrors = () => {
      formErrors.username = "";
      formErrors.email = "";
      saveError.value = "";
    };

    const validateForm = () => {
      clearErrors();
      let isValid = true;

      // Username validation
      if (!editForm.username.trim()) {
        formErrors.username = "Nome de usuário é obrigatório";
        isValid = false;
      } else if (editForm.username.length < 3) {
        formErrors.username =
          "Nome de usuário deve ter pelo menos 3 caracteres";
        isValid = false;
      } else if (!/^[a-zA-Z0-9_]+$/.test(editForm.username)) {
        formErrors.username =
          "Nome de usuário pode conter apenas letras, números e underscore";
        isValid = false;
      }

      // Email validation
      if (!editForm.email.trim()) {
        formErrors.email = "Email é obrigatório";
        isValid = false;
      } else if (!/\S+@\S+\.\S+/.test(editForm.email)) {
        formErrors.email = "Email inválido";
        isValid = false;
      }

      return isValid;
    };

    // Check if form has changes
    const hasChanges = computed(() => {
      return (
        editForm.username !== (user.value?.username || "") ||
        editForm.email !== (user.value?.email || "")
      );
    });

    // Check if form can be saved
    const canSave = computed(() => {
      return hasChanges.value && !formErrors.username && !formErrors.email;
    });

    const startEditing = () => {
      resetFormToUserData();
      isEditing.value = true;
    };

    const cancelEditing = () => {
      isEditing.value = false;
      resetFormToUserData();
      clearErrors();
    };

    const handleSave = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;
      clearErrors();

      try {
        const result = await authStore.updateProfile({
          username: editForm.username.trim(),
          email: editForm.email.trim(),
        });

        if (result.success) {
          // Profile updated successfully
          user.value = authStore.userProfile;
          isEditing.value = false;
        } else {
          saveError.value = result.error || "Erro ao atualizar perfil";
        }
      } catch (error) {
        saveError.value = "Erro ao atualizar perfil. Tente novamente.";
        console.error("Profile update error:", error);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    };

    const triggerImageUpload = () => {
      if (fileInput.value) {
        fileInput.value.click();
      }
    };

    const handleImageUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Validar tipo de arquivo
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        saveError.value = "Formato de imagem inválido. Use JPG, PNG ou GIF.";
        return;
      }

      // Validar tamanho (5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        saveError.value = "Imagem muito grande. Tamanho máximo: 5MB.";
        return;
      }

      uploadingImage.value = true;
      saveError.value = "";
      uploadSuccess.value = "";

      try {
        const result = await authStore.updateProfileImage(file);

        if (result.success) {
          // Atualizar user local
          user.value = authStore.userProfile;

          // Limpar input
          if (fileInput.value) {
            fileInput.value.value = "";
          }

          // Mostrar mensagem de sucesso
          uploadSuccess.value = "Imagem atualizada com sucesso!";
          setTimeout(() => (uploadSuccess.value = ""), 3000);
        } else {
          saveError.value = result.error || "Erro ao fazer upload da imagem";
          setTimeout(() => (saveError.value = ""), 3000);
        }
      } catch (error) {
        saveError.value = "Erro ao fazer upload. Tente novamente.";
        setTimeout(() => (saveError.value = ""), 3000);
        console.error("Upload error:", error);
      } finally {
        uploadingImage.value = false;
      }
    };

    const removeImage = async () => {
      if (!confirm("Tem certeza que deseja remover sua foto de perfil?")) {
        return;
      }

      uploadingImage.value = true;
      saveError.value = "";
      uploadSuccess.value = "";

      try {
        const result = await authStore.removeProfileImage();

        if (result.success) {
          user.value = authStore.userProfile;
          uploadSuccess.value = "Imagem removida com sucesso!";
          setTimeout(() => (uploadSuccess.value = ""), 3000);
        } else {
          saveError.value = result.error || "Erro ao remover imagem";
          setTimeout(() => (saveError.value = ""), 3000);
        }
      } catch (error) {
        saveError.value = "Erro ao remover imagem. Tente novamente.";
        setTimeout(() => (saveError.value = ""), 3000);
        console.error("Remove image error:", error);
      } finally {
        uploadingImage.value = false;
      }
    };

    onMounted(async () => {
      loading.value = true;
      await authStore.fetchProfile();
      user.value = authStore.userProfile;
      loading.value = false;
    });

    return {
      authStore,
      user,
      loading,
      isEditing,
      editForm,
      formErrors,
      saveError,
      uploadSuccess,
      hasChanges,
      canSave,
      uploadingImage,
      fileInput,
      startEditing,
      cancelEditing,
      handleSave,
      formatDate,
      resetFormToUserData,
      clearErrors,
      validateForm,
      profileImageUrl,
      triggerImageUpload,
      handleImageUpload,
      removeImage,
    };
  },
};
</script>

<style scoped>
/* Container */
.profile-container {
  position: relative;
  min-height: 100vh;
  padding: 2rem 1rem;
}

/* Animated Background */
.profile-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background: linear-gradient(135deg, #f5f7fa 0%, #f0f4f8 100%);
  overflow: hidden;
}

.bg-shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.2;
  animation: float 20s ease-in-out infinite;
}

.shape-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  top: -100px;
  right: -100px;
}

.shape-2 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  bottom: -100px;
  left: -100px;
  animation-delay: 5s;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(30px, -30px);
  }
}

/* Content */
.profile-content {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

/* Header */
.profile-header {
  margin-bottom: 3rem;
  animation: slideDown 0.6s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-content {
  text-align: center;
}

.profile-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1a202c;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: bounce 2s ease-in-out infinite;
}

.title-icon svg {
  width: 28px;
  height: 28px;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.profile-subtitle {
  color: #64748b;
  font-size: 1.125rem;
}

/* Grid */
.profile-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

@media (min-width: 768px) {
  .profile-grid {
    grid-template-columns: 1fr 1.2fr;
  }
}

/* Cards */
.profile-card {
  position: relative;
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  animation: fadeInUp 0.6s ease-out backwards;
}

.main-card {
  animation-delay: 0.1s;
}

.edit-card {
  animation-delay: 0.2s;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.profile-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.card-glow {
  position: absolute;
  inset: 0;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1),
    rgba(37, 99, 235, 0.1)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.profile-card:hover .card-glow {
  opacity: 1;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
}

.spinner-large {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 1rem;
  color: #64748b;
  font-weight: 500;
}

/* Profile Info */
.profile-info {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Avatar Section */
.avatar-section {
  text-align: center;
  margin-bottom: 2rem;
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
  margin-bottom: 1.5rem;
}

.avatar-ring {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  animation: rotate 3s linear infinite;
  z-index: -1;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.avatar-image,
.avatar-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid white;
}

.avatar-image {
  object-fit: cover;
}

.avatar-placeholder {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-letter {
  color: white;
  font-size: 3rem;
  font-weight: 700;
}

.avatar-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  background: #10b981;
  border-radius: 50%;
  border: 3px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: pulse 2s ease-in-out infinite;
}

.avatar-badge svg {
  width: 20px;
  height: 20px;
}

/* Avatar Upload Button */
.avatar-upload-btn {
  position: absolute;
  bottom: 0;
  right: -8px;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 50%;
  border: 3px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
}

.avatar-upload-btn:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.6);
}

.avatar-upload-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.avatar-upload-btn svg {
  width: 20px;
  height: 20px;
}

/* Avatar Remove Button */
.avatar-remove-btn {
  position: absolute;
  top: 0;
  right: -8px;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 50%;
  border: 3px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  animation: fadeIn 0.3s ease-out;
}

.avatar-remove-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.6);
}

.avatar-remove-btn svg {
  width: 18px;
  height: 18px;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.user-info {
  margin-bottom: 1.5rem;
}

.user-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.user-email {
  color: #64748b;
  font-size: 1rem;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  gap: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.stat-card:hover {
  border-color: #3b82f6;
  transform: translateX(5px);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon svg {
  width: 24px;
  height: 24px;
}

.stat-icon-calendar {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.stat-info {
  flex: 1;
}

.stat-label {
  color: #64748b;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.stat-value {
  color: #1a202c;
  font-weight: 600;
  font-size: 1rem;
}

/* Card Header */
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f1f5f9;
}

.card-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon-small {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.title-icon-small svg {
  width: 18px;
  height: 18px;
}

.edit-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.edit-btn svg {
  width: 16px;
  height: 16px;
}

/* View Mode */
.view-mode {
  animation: fadeIn 0.3s ease-out;
}

.info-group {
  display: grid;
  gap: 1.5rem;
}

.info-item {
  animation: slideInLeft 0.4s ease-out backwards;
}

.info-item:nth-child(1) {
  animation-delay: 0.1s;
}
.info-item:nth-child(2) {
  animation-delay: 0.2s;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.info-label {
  display: block;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #1a202c;
  font-weight: 500;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.info-value:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.value-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-weight: 600;
}

.value-icon svg {
  width: 18px;
  height: 18px;
}

/* Edit Form */
.edit-form {
  animation: fadeIn 0.3s ease-out;
}

.form-grid {
  display: grid;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-group {
  animation: slideInLeft 0.4s ease-out backwards;
}

.form-group:nth-child(1) {
  animation-delay: 0.1s;
}
.form-group:nth-child(2) {
  animation-delay: 0.2s;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.label-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-weight: 700;
}

.label-icon svg {
  width: 16px;
  height: 16px;
}

.input-wrapper {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  color: #1a202c;
  background: white;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.input-error {
  border-color: #ef4444 !important;
}

.input-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
}

.error-message {
  margin-top: 0.5rem;
  color: #ef4444;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

/* Error Alert */
.error-alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #ef4444;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  animation: shake 0.5s ease-in-out;
}

/* Success Alert */
.success-alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  color: #22c55e;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease-out;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}

.error-alert svg,
.success-alert svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  animation: fadeIn 0.3s ease-out 0.3s backwards;
}

.btn-cancel,
.btn-save {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.btn-cancel {
  background: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-cancel:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-save {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-content svg {
  width: 18px;
  height: 18px;
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Responsive */
@media (max-width: 768px) {
  .profile-title {
    font-size: 2rem;
  }

  .title-icon {
    width: 40px;
    height: 40px;
  }

  .title-icon svg {
    width: 24px;
    height: 24px;
  }

  .avatar-image,
  .avatar-placeholder {
    width: 100px;
    height: 100px;
  }

  .avatar-letter {
    font-size: 2.5rem;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-cancel,
  .btn-save {
    width: 100%;
  }
}
</style>
